import { type ECharts } from 'echarts';

export const BUILTIN_OBJECT = {
  '[object Function]': true,
  '[object RegExp]': true,
  '[object Date]': true,
  '[object Error]': true,
  '[object CanvasGradient]': true,
  '[object CanvasPattern]': true,
  // For node-canvas
  '[object Image]': true,
  '[object Canvas]': true
};

export function normalizeToArray<T>(value?: T | T[]): T[] {
  if (value instanceof Array) {
    return value;
  }
  if (value == null || undefined) {
    return [];
  }
  return [value];
}

export function isObject<T = unknown>(value: T): value is object & T {
  const type = typeof value;
  return type === 'function' || (!!value && type === 'object');
}

export function isArray(value: unknown): value is unknown[] {
  if (Array.isArray) {
    return Array.isArray(value);
  }
  return Object.prototype.toString.call(value) === '[object Array]';
}

export function isBuiltInObject(value: unknown): boolean {
  return !!BUILTIN_OBJECT[Object.prototype.toString.call(value)];
}

export function getRange(arr: Array<number>): Array<number> {
  if (arr.length === 0) {
    return [];
  }
  if (arr.length === 1) {
    return [arr[0], arr[0]];
  }
  const range = [arr[0], arr[0]];
  arr.forEach(item => {
    if (item < range[0]) {
      range[0] = item;
    }
    if (item > range[1]) {
      range[1] = item;
    }
  });
  return range;
}

export function getURLParam(name: string): string | null {
  const TWO = 2;
  // eslint-disable-next-line no-restricted-globals
  const matches = new RegExp(`[?|&]${name}=([^&;]+?)(&|#|;|$)`, 'ig').exec(location.search);
  if (!matches || matches.length < TWO || !matches[1]) {
    return null;
  }
  return matches[1].replace(/\+/g, '%20');
}

export function measureTextWidth(
  text: string,
  fontSize: number,
  fontFamily = '',
  fontWeight: string | number = ''
) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  return ctx.measureText(text).width;
}

export function getComponentModelsByName(ecIns: ECharts, name: string) {
  let count = 0;
  const result = [];
  while (true) {
    const modelItem = ecIns.getModel().getComponent(name, count);
    if (modelItem) {
      result.push(modelItem);
      count++;
    } else {
      break;
    }
  }
  return result;
}

export function getComponentViewsByModels(ecIns: ECharts, model) {
  const models = Array.isArray(model) ? model : [model];
  return models.map(item => ecIns.getViewOfComponentModel(item));
}

/**
 * 比较两个数组内容是否一致
 * @param arr1
 * @param arr2
 * @returns
 */
export function equalsArray(arr1: Array<unknown>, arr2: Array<unknown>): boolean {
  if (!arr1 || !arr2) {
    return false;
  }

  if (arr1.length !== arr2.length) {
    return false;
  }

  for (let i = 0, len = arr1.length; i < len; i++) {
    if (isArray(arr1[i]) && isArray(arr2[i])) {
      if (!equalsArray(arr1[i] as unknown[], arr2[i] as unknown[])) {
        return false;
      }
    } else if (arr1[i] !== arr2[i]) {
      return false;
    }
  }
  return true;
}

/**
 * 树型结构数据的通用遍历工具
 * @description 迭代方式遍历  前序遍历
 * @param root 树的根节点
 * @param callBackFn 遍历方法
 * @param iteratFn 获取子节点的方法
 * @returns void
 */
export function treeIterator<T>(
  root: T,
  callBackFn: (node: T) => void,
  iteratFn: (node: T) => T[] | T | undefined
): void {
  if (root == null) {
    return;
  }
  // 建栈存点
  const nodeStack: Array<T> = [];
  // 根节点入栈
  nodeStack.push(root);
  // 遍历
  while (nodeStack.length) {
    // 出栈
    const curNode = nodeStack.pop();
    // 当前节点执行遍历方法
    callBackFn(curNode);
    // 获取当前节点的子节点
    const children = normalizeToArray(iteratFn(curNode));
    // 子节点入栈
    if (children) {
      nodeStack.push(...children);
    }
  }
}

// 判断是否为移动端
export function isMobile(): boolean {
  return /Android|webOS|iPhone|iPod|BlackBerry/i.test(navigator.userAgent);
}
